import { OVERVIEW_PICTURE_MOST, chunkOrder, chunksToDraw, chunksTouched, type AssetManifest, type CellRect, type ChunkRef } from "daedalus-core";
import { paintPictureChunk, type PictureAtlas, type PictureLayer, type PicturePlaceholder, type PictureSurface } from "./map-picture.ts";

/**
 * The cache of map pictures (ADR-109's amendment of 2026-10-13, decisions 36, 38 and 39): one picture a map and
 * scale, kept by map id, scale and layer revision, composed a few chunks a step, redrawn from the chunks a repaint
 * touches, queued again whole when its canvas loses its context, and freed with the document. It holds no game
 * state: a request carries the map's size and a feed that gives the layers of any chunk, so the editor feeds it
 * from its own layers and a game page from a compiled client chunk.
 */

/**
 * The most chunks a step composes unless the cache is told otherwise (decision 39's four a frame).
 */
export const PICTURE_CHUNKS_PER_STEP = 4;

/**
 * What the cache needs to make or find a map's picture. `feed` gives the layers of a chunk in drawn order, each
 * time the chunk is composed. A map longer than `OVERVIEW_PICTURE_MOST` pixels at the scale gets a picture of that
 * size cropped around `centre`, a tile of the map, the middle of the map when omitted.
 */
export interface PictureRequest {
  readonly map: string;
  readonly scale: number;
  readonly revision: number;
  readonly width: number;
  readonly height: number;
  readonly chunkSize: number;
  readonly tileSize: number;
  readonly manifest: AssetManifest;
  readonly feed: (cx: number, cy: number) => readonly PictureLayer[];
  readonly centre?: { readonly x: number; readonly y: number };
}

/**
 * A map's picture: its canvas, what it was made for, and a version that moves whenever its pixels may have changed,
 * so a texture made from the canvas is refreshed only then.
 */
export interface MapPicture<I> {
  readonly map: string;
  readonly scale: number;
  readonly revision: number;
  readonly surface: PictureSurface<I>;
  readonly chunksWide: number;
  readonly chunksHigh: number;
  readonly crop: { readonly x: number; readonly y: number };
  version: number;
}

/**
 * A picture and the chunks it still owes.
 */
interface Entry<I> {
  readonly picture: MapPicture<I>;
  readonly request: PictureRequest;
  readonly pending: Map<number, ChunkRef>;
  readonly incomplete: Map<number, ChunkRef>;
  readonly first: { readonly cx: number; readonly cy: number; readonly cx1: number; readonly cy1: number };
}

/**
 * The key a map's picture at a scale is kept under.
 *
 * @param map Map id.
 * @param scale Pixels a tile.
 * @returns The key.
 */
function keyOf(map: string, scale: number): string {
  return `${map}|${scale}`;
}

/**
 * Where a picture's crop starts along one axis: the whole map when it fits, else the window centred on a tile and
 * kept inside the map.
 *
 * @param tiles The map's size in tiles along the axis.
 * @param scale Pixels a tile.
 * @param centre The tile to centre on, or undefined for the middle.
 * @returns The first tile of the crop.
 */
function cropStart(tiles: number, scale: number, centre: number | undefined): number {
  const shown = Math.min(tiles, Math.floor(OVERVIEW_PICTURE_MOST / scale));
  if (shown >= tiles) {
    return 0;
  }
  const middle = centre ?? Math.floor(tiles / 2);
  return Math.max(0, Math.min(tiles - shown, Math.round(middle - shown / 2)));
}

/**
 * Keeps the pictures of the maps a view draws.
 */
export class MapPictureCache<I> {
  private readonly entries = new Map<string, Entry<I>>();

  /**
   * @param surfaces Makes a canvas of a size in pixels, or returns null when the browser cannot.
   * @param atlas The loaded atlas pages the pictures copy from.
   * @param perStep The most chunks a step composes.
   * @param placeholder The colours a chunk with an atlas page not loaded is laid over, or null for none.
   */
  public constructor(
    private readonly surfaces: (width: number, height: number) => PictureSurface<I> | null,
    private readonly atlas: PictureAtlas<I>,
    private readonly perStep = PICTURE_CHUNKS_PER_STEP,
    private readonly placeholder: PicturePlaceholder | null = null,
  ) {}

  /**
   * The number of pictures held.
   *
   * @returns The count.
   */
  public get size(): number {
    return this.entries.size;
  }

  /**
   * Finds or makes the picture a request names. A picture of the same map, scale and revision is returned as it is;
   * one of another revision is freed and replaced by an empty one with every chunk queued, the chunks nearest
   * `from` first.
   *
   * @param request The map, its size and its feed.
   * @param from The chunk to compose first, the map's middle chunk when omitted.
   * @returns The picture, or null when no canvas can be made.
   * @throws RangeError when the scale, a size or a chunk size is not a positive whole number.
   */
  public request(request: PictureRequest, from?: ChunkRef): MapPicture<I> | null {
    for (const [value, name] of [[request.scale, "scale"], [request.width, "width"], [request.height, "height"], [request.chunkSize, "chunkSize"], [request.tileSize, "tileSize"]] as const) {
      if (!Number.isInteger(value) || value < 1) {
        throw new RangeError(`${name} must be a positive whole number, got ${value}`);
      }
    }
    const key = keyOf(request.map, request.scale);
    const held = this.entries.get(key);
    if (held !== undefined && held.picture.revision === request.revision) {
      return held.picture;
    }
    if (held !== undefined) {
      this.free(key, held);
    }
    const x0 = cropStart(request.width, request.scale, request.centre?.x);
    const y0 = cropStart(request.height, request.scale, request.centre?.y);
    const shownW = Math.min(request.width, Math.floor(OVERVIEW_PICTURE_MOST / request.scale));
    const shownH = Math.min(request.height, Math.floor(OVERVIEW_PICTURE_MOST / request.scale));
    const surface = this.surfaces(shownW * request.scale, shownH * request.scale);
    if (surface === null) {
      return null;
    }
    const chunksWide = Math.ceil(request.width / request.chunkSize);
    const chunksHigh = Math.ceil(request.height / request.chunkSize);
    const picture: MapPicture<I> = { map: request.map, scale: request.scale, revision: request.revision, surface, chunksWide, chunksHigh, crop: { x: x0, y: y0 }, version: 0 };
    const entry: Entry<I> = {
      picture,
      request,
      pending: new Map(),
      incomplete: new Map(),
      first: { cx: Math.floor(x0 / request.chunkSize), cy: Math.floor(y0 / request.chunkSize), cx1: Math.floor((x0 + shownW - 1) / request.chunkSize), cy1: Math.floor((y0 + shownH - 1) / request.chunkSize) },
    };
    this.entries.set(key, entry);
    const requeue = (): void => {
      this.queueAll(entry);
      picture.version += 1;
    };
    surface.onLost(requeue);
    surface.onRestored(requeue);

    this.queueAll(entry, from);
    return picture;
  }

  /**
   * The picture held for a map at a scale.
   *
   * @param map Map id.
   * @param scale Pixels a tile.
   * @returns The picture, or null.
   */
  public picture(map: string, scale: number): MapPicture<I> | null {
    return this.entries.get(keyOf(map, scale))?.picture ?? null;
  }

  /**
   * Whether a picture is held for a map at a scale.
   *
   * @param map Map id.
   * @param scale Pixels a tile.
   * @returns True when held.
   */
  public has(map: string, scale: number): boolean {
    return this.entries.has(keyOf(map, scale));
  }

  /**
   * Chunks of a picture waiting to be composed.
   *
   * @param map Map id.
   * @param scale Pixels a tile.
   * @returns The count, 0 for a picture not held.
   */
  public pending(map: string, scale: number): number {
    return this.entries.get(keyOf(map, scale))?.pending.size ?? 0;
  }

  /**
   * Chunks of a picture composed with the placeholder because an atlas page was not loaded, waiting for
   * `retryIncomplete`.
   *
   * @param map Map id.
   * @param scale Pixels a tile.
   * @returns The count, 0 for a picture not held.
   */
  public incomplete(map: string, scale: number): number {
    return this.entries.get(keyOf(map, scale))?.incomplete.size ?? 0;
  }

  /**
   * Queues every chunk of a picture, nearest a chunk first.
   *
   * @param entry The picture's entry.
   * @param from The chunk to start from, the middle of the crop when omitted.
   */
  private queueAll(entry: Entry<I>, from?: ChunkRef): void {
    const { first, picture } = entry;
    const start = from ?? { cx: Math.floor((first.cx + first.cx1) / 2), cy: Math.floor((first.cy + first.cy1) / 2) };
    entry.pending.clear();
    entry.incomplete.clear();
    for (const chunk of chunkOrder(picture.chunksWide, picture.chunksHigh, start)) {
      if (chunk.cx >= first.cx && chunk.cx <= first.cx1 && chunk.cy >= first.cy && chunk.cy <= first.cy1) {
        entry.pending.set(chunk.cy * picture.chunksWide + chunk.cx, chunk);
      }
    }
  }

  /**
   * Queues the chunks of every picture of a map that a rectangle of repainted cells touches. A chunk already
   * queued keeps its place; one composed with the placeholder is queued again.
   *
   * @param map Map id.
   * @param rect The cells, `x0` and `y0` inclusive, `x1` and `y1` exclusive.
   * @returns How many chunks of one picture the rectangle touches, the most over the map's pictures; 0 for a map
   *   with none held, an empty rectangle or one outside the map.
   */
  public markDirty(map: string, rect: CellRect): number {
    let most = 0;
    for (const entry of this.entries.values()) {
      if (entry.picture.map !== map) {
        continue;
      }
      const { first, picture, request } = entry;
      let count = 0;
      for (const chunk of chunksTouched(rect, request.chunkSize, picture.chunksWide, picture.chunksHigh)) {
        if (chunk.cx < first.cx || chunk.cx > first.cx1 || chunk.cy < first.cy || chunk.cy > first.cy1) {
          continue;
        }
        const id = chunk.cy * picture.chunksWide + chunk.cx;
        entry.incomplete.delete(id);
        if (!entry.pending.has(id)) {
          entry.pending.set(id, chunk);
        }
        count += 1;
      }
      most = Math.max(most, count);
    }
    return most;
  }

  /**
   * Queues every chunk of every picture of a map again, for a change the cache cannot size: a layer shown or hidden.
   *
   * @param map Map id.
   */
  public markAllDirty(map: string): void {
    for (const entry of this.entries.values()) {
      if (entry.picture.map === map) {
        this.queueAll(entry, undefined);
        entry.picture.version += 1;
      }
    }
  }

  /**
   * Moves the chunks composed with the placeholder back to the queue, for when the atlas page they lacked is loaded.
   *
   * @param map Map id.
   * @param scale Pixels a tile.
   * @returns How many chunks were queued.
   */
  public retryIncomplete(map: string, scale: number): number {
    const entry = this.entries.get(keyOf(map, scale));
    if (entry === undefined) {
      return 0;
    }
    const count = entry.incomplete.size;
    for (const [id, chunk] of entry.incomplete) {
      entry.pending.set(id, chunk);
    }
    entry.incomplete.clear();
    return count;
  }

  /**
   * Composes up to a step's share of the chunks a picture owes, the oldest queued first. A chunk with an atlas page
   * not loaded is painted over the placeholder and set aside as incomplete. A chunk whose feed or paint throws is set
   * aside too and the error goes on to the caller, so a bad chunk is not tried again every frame.
   *
   * @param map Map id.
   * @param scale Pixels a tile.
   * @param near The chunk to compose nearest first (the one under the pointer's last place, when a stroke is being
   *   drawn), or omitted for the oldest queued first.
   * @returns How many chunks were composed.
   * @throws Error whatever the chunk's feed or a cell of it throws.
   */
  public step(map: string, scale: number, near?: ChunkRef): number {
    const entry = this.entries.get(keyOf(map, scale));
    if (entry === undefined) {
      return 0;
    }
    const { picture, request } = entry;
    let composed = 0;
    if (entry.pending.size === 0) {
      return 0;
    }
    const order: ChunkRef[] = [];
    if (near === undefined) {
      for (const chunk of entry.pending.values()) {
        if (order.length >= this.perStep) {
          break;
        }
        order.push(chunk);
      }
    } else {
      order.push(...chunksToDraw([...entry.pending.values()], near, this.perStep));
    }
    for (const chunk of order) {
      const id = chunk.cy * picture.chunksWide + chunk.cx;
      entry.pending.delete(id);
      let layers: readonly PictureLayer[];
      try {
        layers = request.feed(chunk.cx, chunk.cy);
      } catch (error) {
        entry.incomplete.set(id, chunk);
        throw error;
      }
      const spec = {
        cx: chunk.cx,
        cy: chunk.cy,
        chunkSize: request.chunkSize,
        tileSize: request.tileSize,
        scale,
        manifest: request.manifest,
        layers,
        originX: (chunk.cx * request.chunkSize - picture.crop.x) * scale,
        originY: (chunk.cy * request.chunkSize - picture.crop.y) * scale,
      };
      try {
        const result = paintPictureChunk(picture.surface.context, this.atlas, spec);
        if (result.unavailable > 0) {
          if (this.placeholder !== null) {
            paintPictureChunk(picture.surface.context, this.atlas, { ...spec, placeholder: this.placeholder });
          }
          entry.incomplete.set(id, chunk);
        }
      } catch (error) {
        entry.incomplete.set(id, chunk);
        throw error;
      } finally {
        picture.version += 1;
      }
      composed += 1;
    }
    return composed;
  }

  /**
   * Frees the surface of a picture and forgets it.
   *
   * @param key The picture's key.
   * @param entry Its entry.
   */
  private free(key: string, entry: Entry<I>): void {
    entry.picture.surface.dispose();
    this.entries.delete(key);
  }

  /**
   * Frees every picture of a map.
   *
   * @param map Map id.
   * @returns How many pictures were freed.
   */
  public drop(map: string): number {
    let count = 0;
    for (const [key, entry] of [...this.entries]) {
      if (entry.picture.map === map) {
        this.free(key, entry);
        count += 1;
      }
    }
    return count;
  }

  /**
   * Frees every picture, as when the editor's document is replaced.
   *
   * @returns How many pictures were freed.
   */
  public clear(): number {
    const count = this.entries.size;
    for (const [key, entry] of [...this.entries]) {
      this.free(key, entry);
    }
    return count;
  }
}
